// @ts-nocheck

import React, { useRef, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { DEFAULT_FLOATING_WTV_HEIGHT } from '../../constants/winamax-tv';
import { useViewportContext } from '../../contexts/ViewportContext';
import { getAppDOMElement } from '../../DOMElements';
import { useWtv, useDesktopHorizontalScroll } from '../../hooks';
import useRouter from '../../hooks/useRouter';
import { getPositionOffset, getDimensions } from '../../lib/core/dom/dimensions';
import { selectMultiviewMode, selectMultiviewUserOption } from '../../selectors/multiview/multiviewPrimary.selector';
import { selectUserPrefsShowWTV } from '../../selectors/user/userPrimary.selector';
import theme from '../../styled-components/dark';
import { isMultiviewRoute } from '../../utils/routes/isMultiview';
import ChatLoader from '../ChatLoader';

const WTV_PLACEHOLDER_TIMEOUT = 500;

const Wrapper = styled.div`
  z-index: ${theme.ZIndices.floatingWtv};
`;

/**
 * Displays a floating wtv on desktop only
 *
 * @return {Node} A wrapper with the `ChatLoader` component
 */
const FloatingWtv = () => {
  const floatingWtvRef = useRef();
  const { location } = useRouter();
  const { placeholderRef, offsetTop, isSticky, setFloatingWtvRef } = useWtv();
  const { width: viewportWidth } = useViewportContext();
  const multiviewMode = useSelector(selectMultiviewMode);
  const multiviewUserOption = useSelector(selectMultiviewUserOption);
  const showWTV = useSelector(selectUserPrefsShowWTV);
  const timer = useRef();

  const isMultiview = isMultiviewRoute(location.pathname);
  const domElem = isSticky && !isMultiview ? window : getAppDOMElement();

  // position the video on the placeholder by default
  const placeWtvOnPlaceholder = useCallback(
    () => {
      const stickyWtv = floatingWtvRef.current;
      const placeholder = placeholderRef?.current;

      if (!stickyWtv || !placeholder) {
        return;
      }

      stickyWtv.style.display = 'none';
      placeholder.style.display = 'block';

      if (placeholder) {
        const { width, height } = getDimensions(placeholder);
        const { top, left } = getPositionOffset(placeholder, domElem);
        stickyWtv.style.top = `${offsetTop !== null && !isMultiview ? offsetTop : top}px`;
        stickyWtv.style.left = `${left}px`;
        stickyWtv.style.width = `${width}px`;
        stickyWtv.style.height = isMultiview ? `${height}px` : `${DEFAULT_FLOATING_WTV_HEIGHT}px`;
        stickyWtv.style.display = 'block';
        stickyWtv.style.position = isSticky && !isMultiview ? 'fixed' : 'absolute';
      }
    },
    // we need viewportWidth dependency to adapt the wtv position when we resize the browser
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [domElem, floatingWtvRef, isMultiview, isSticky, offsetTop, placeholderRef, viewportWidth],
  );

  // position the winamax tv on the placeholder by default
  useEffect(
    () => {
      if (multiviewMode) {
        // we use a timeout here because at the first load of the multiview section, the iframe doesn't fit well
        // ...with the placeholder
        timer.current = setTimeout(placeWtvOnPlaceholder, WTV_PLACEHOLDER_TIMEOUT);
      } else {
        placeWtvOnPlaceholder();
      }

      return () => {
        if (timer.current) {
          clearTimeout(timer.current);
        }
      };
    },
    // use of `multiviewMode` to adapt the winamax tv dimension for when we go to multiview
    // use of `multiviewUserOption` to adapt the winamax tv dimension for when the user switches multiview option
    [placeWtvOnPlaceholder, multiviewUserOption, multiviewMode],
  );

  // replaces the WTV to its correct position after the wheel event occures
  useDesktopHorizontalScroll(placeWtvOnPlaceholder, 100);

  // set a ref for the floating wtv component. That ref (= floatingWtvRef) will be used in the `RightColumn` component
  // case of use: for when we are on sticky mode and that the basket height is superior than the browser height
  useEffect(() => {
    if (floatingWtvRef.current) {
      setFloatingWtvRef(floatingWtvRef);
    }
  }, [showWTV, setFloatingWtvRef]);

  return showWTV ? (
    <Wrapper ref={floatingWtvRef}>
      <ChatLoader />
    </Wrapper>
  ) : null;
};

export default FloatingWtv;
